import { useState } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import AuthLayout, { useFormState } from '../components/AuthLayout'
import PasswordField from '../components/PasswordField'
import Alert from '../components/Alert'
import Spinner from '../components/Spinner'
import DepartmentSetup from '../components/DepartmentSetup'
import { signupSetupToken } from '../api/auth'
import { authErrorMessage } from '../lib/errors'
import { useAuth } from '../auth/AuthContext'

export default function Login() {
  const form = useFormState({ email: '', password: '' })
  const [setupToken, setSetupToken] = useState(null)
  const { signIn } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [params] = useSearchParams()

  // Where the guard bounced us from, so a deep link survives the login round-trip.
  // Keep the query too: email links such as /offers?offer=<id> need it after sign-in.
  const fromLocation = location.state?.from
  const from = fromLocation?.pathname
    ? `${fromLocation.pathname}${fromLocation.search || ''}${fromLocation.hash || ''}`
    : '/'
  const expired = params.get('expired') === '1'

  async function onSubmit(e) {
    e.preventDefault()
    const errors = {}
    if (!form.values.email.trim()) errors.email = 'Email is required'
    if (!form.values.password) errors.password = 'Password is required'
    form.setErrors(errors)
    if (Object.keys(errors).length) return

    form.setAlert(null)
    form.setBusy(true)
    try {
      await signIn(form.values.email.trim(), form.values.password)
      // '/' lets HomeRedirect pick the first screen this role is permitted.
      navigate(from === '/dashboard' ? '/' : from, { replace: true })
    } catch (err) {
      // Approval pending: a Department Manager who hasn't picked departments does it now.
      if (err?.status === 403) {
        try {
          const res = await signupSetupToken(form.values.email.trim(), form.values.password)
          if (res?.data?.setup_token) {
            setSetupToken(res.data.setup_token)
            form.setBusy(false)
            return
          }
        } catch { /* fall through to the pending message */ }
      }
      form.setAlert({ type: 'danger', message: authErrorMessage(err, 'Could not sign in.') })
      form.setBusy(false)
    }
  }

  if (setupToken) {
    return (
      <AuthLayout title="Choose your departments" subtitle="Your email is confirmed. Pick the departments you manage, then an admin approves your account.">
        <DepartmentSetup setupToken={setupToken} />
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Welcome back to TalentFlow."
      foot={
        <>
          New here?{' '}
          <Link className="link-btn" to="/auth/signup">
            Create an account
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate>
        <Alert type={form.alert?.type}>{form.alert?.message}</Alert>
        {!form.alert && expired ? (
          <Alert type="danger">Your session expired. Please sign in again.</Alert>
        ) : null}

        <div className="form-field">
          <label htmlFor="login-email">
            Email<span className="req"> *</span>
          </label>
          <input
            id="login-email"
            type="email"
            autoComplete="email"
            value={form.values.email}
            onChange={(e) => form.setField('email', e.target.value)}
            className={form.errors.email ? 'err' : ''}
            placeholder="you@utopiabrands.com"
            disabled={form.busy}
            required
          />
          <span className={`field-error${form.errors.email ? ' show' : ''}`}>
            {form.errors.email || ''}
          </span>
        </div>

        <PasswordField
          id="login-password"
          value={form.values.password}
          onChange={(v) => form.setField('password', v)}
          error={form.errors.password}
          disabled={form.busy}
        />

        <div className="auth-row-end">
          <Link className="link-btn" to="/auth/forgot-password">
            Forgot password?
          </Link>
        </div>

        <button type="submit" className="btn btn-primary btn-block" disabled={form.busy}>
          {form.busy ? <Spinner label="Signing in" /> : null}
          {form.busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </AuthLayout>
  )
}
